import React, { useState, useEffect, useContext } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    Switch,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EventIcon from '@mui/icons-material/Event';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { useAppStateContext } from '../../state/AppContextProvider';
import { BattleCardProps, AgentSection, AgentResponse, CurrentSection, ListItem } from '../../common/models';
import { 
    CalendarEventCreationDialog, 
    CalendarAccount, 
    CalendarEvent, 
    MessageContent 
} from '../Calendar';
import { CalendarProvider } from '../Calendar/types';
import { HttpServiceContext } from '../../state/HttpProvider';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`battle-card-tabpanel-${index}`}
            aria-labelledby={`battle-card-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ height: '100%' }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const BattleCard: React.FC<BattleCardProps> = ({ open, onClose }) => {
    // Feature toggle for calendar functionality
    const CALENDAR_ENABLED = false; // Set to true to enable calendar features
    
    // Content metrics calculation using backend API
    const [contentMetrics, setContentMetrics] = useState<any>(null);
    const [metricsLoading, setMetricsLoading] = useState(false);

    const calculateContentMetrics = async () => {
        // Don't run content analysis if single page restriction is disabled
        if (!singlePageRestriction) {
            setContentMetrics(null);
            return;
        }
        
        const selectedSections = getSelectedSections();
        
        if (selectedSections.length === 0) {
            setContentMetrics(null);
            return;
        }

        setMetricsLoading(true);
        try {
            // Call backend API for accurate content analysis
            const backendMetrics = await httpService.postJson<any, any>(
                '/enhanced-export/battle-card/analyze',
                {
                    sessionId: state.currentConversation?.sessionId,
                    title: `Battle Card - ${new Date().toLocaleDateString()}`,
                    sections: selectedSections,
                    options: {
                        singlePageRestriction,
                        allowTruncation
                    }
                },
                new AbortController().signal
            );

            if (backendMetrics) {
                
                // Calculate usage percentages
                const characterUsagePercent = Math.min((backendMetrics.totalCharacters / backendMetrics.limits.maxCharacters) * 100, 100);
                const pageUsagePercent = backendMetrics.willFitOnOnePage ? 
                    Math.min((backendMetrics.estimatedHeight / 722) * 100, 100) : 100; // 722 is available height
                const sectionUsagePercent = Math.min((backendMetrics.sectionCount / backendMetrics.limits.maxSections) * 100, 100);
                
                const metrics = {
                    ...backendMetrics,
                    characterUsagePercent,
                    pageUsagePercent,
                    sectionUsagePercent,
                    isNearLimit: characterUsagePercent > 80 || pageUsagePercent > 80,
                    isOverLimit: !backendMetrics.willFitOnOnePage || characterUsagePercent >= 100,
                    remainingCharacters: Math.max(backendMetrics.limits.maxCharacters - backendMetrics.totalCharacters, 0),
                    estimatedPages: backendMetrics.willFitOnOnePage ? 1 : Math.ceil(backendMetrics.estimatedHeight / 722)
                };
                
                setContentMetrics(metrics);
            }
        } catch (error) {
            console.error('Error calculating content metrics:', error);
            // Fallback to simple client-side calculation
            const selectedSections = getSelectedSections();
            let totalCharacters = 0;
            selectedSections.forEach(section => {
                totalCharacters += section.content.length;
            });
            
            const estimatedPages = Math.ceil(totalCharacters / 3500);
            const characterUsagePercent = Math.min((totalCharacters / 3500) * 100, 100);
            
            setContentMetrics({
                totalCharacters,
                totalSections: selectedSections.length,
                estimatedPages,
                characterUsagePercent,
                pageUsagePercent: Math.min((estimatedPages / 1) * 100, 100),
                sectionUsagePercent: Math.min((selectedSections.length / 8) * 100, 100),
                isNearLimit: characterUsagePercent > 80,
                isOverLimit: characterUsagePercent >= 100 || estimatedPages > 1,
                willFitOnOnePage: estimatedPages <= 1,
                limits: { maxCharacters: 3500, maxSections: 8 },
                recommendations: estimatedPages > 1 ? ['Content may exceed one page'] : ['Content should fit on one page']
            });
        } finally {
            setMetricsLoading(false);
        }
    };

    const getUsageColor = (percentage: number) => {
        if (percentage < 50) return '#4caf50'; // Green
        if (percentage < 80) return '#ff9800'; // Orange
        return '#f44336'; // Red
    };

    const getListItemStats = () => {
        let totalListItems = 0;
        let selectedListItems = 0;
        
        agentResponses.forEach(agent => {
            agent.sections.forEach(section => {
                if (section.selected && section.listItems) {
                    totalListItems += section.listItems.length;
                    selectedListItems += section.listItems.filter(item => item.selected).length;
                }
            });
        });
        
        if (totalListItems === 0) return '';
        return `${selectedListItems}/${totalListItems} items`;
    };
    
    const { state, getConversationBySessionId, exportBattleCard } = useAppStateContext();
    const { httpService } = useContext(HttpServiceContext);
    const [agentResponses, setAgentResponses] = useState<AgentResponse[]>([]);

    // Content restriction toggles
    const [singlePageRestriction, setSinglePageRestriction] = useState(true);
    const [allowTruncation] = useState(false); // Hidden from UI - always disabled
    const [contentAnalysisExpanded, setContentAnalysisExpanded] = useState(false);
    
    // Add state for selected agent
    const [selectedAgentIndex, setSelectedAgentIndex] = useState(0);
    
    // Track screen size for mobile responsiveness
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    // Trigger content analysis when selections or toggles change
    useEffect(() => {
        if (open) {
            calculateContentMetrics();
        }
    }, [open, singlePageRestriction, allowTruncation, agentResponses.map(agent => 
        agent.sections.map(section => {
            // Track both section selection and individual list item selections
            const sectionState = section.selected;
            const listItemState = section.listItems?.map(item => `${item.id}:${item.selected}`).join('|') || '';
            return `${sectionState}-${listItemState}`;
        }).join(',')
    ).join('|')]);
    
    const getSectionImpact = (agentIndex: number, sectionIndex: number) => {
        const section = agentResponses[agentIndex]?.sections[sectionIndex];
        if (!section || !section.selected) return null;
        
        // Calculate the actual content that would be exported based on selections
        let actualContent = '';
        
        if (section.hasLists && section.listItems && section.processedContent !== undefined) {
            const selectedItems = section.listItems.filter(item => item.selected);
            const hasTextContent = section.processedContent.trim().length > 0;
            
            if (hasTextContent && selectedItems.length > 0) {
                const selectedListContent = selectedItems.map(item => `• ${item.content}`).join('\n');
                actualContent = section.processedContent.trim() + '\n\n' + selectedListContent;
            } else if (hasTextContent && selectedItems.length === 0) {
                actualContent = section.processedContent.trim();
            } else if (!hasTextContent && selectedItems.length > 0) {
                actualContent = selectedItems.map(item => `• ${item.content}`).join('\n');
            } else {
                actualContent = section.content;
            }
        } else {
            actualContent = section.content || '';
        }
        
        const characterCount = actualContent.length;
        const estimatedLines = Math.ceil(characterCount / 80); // ~80 chars per line
        const estimatedPageContribution = characterCount / 3500; // Rough page estimation
        
        return {
            characterCount,
            estimatedLines,
            estimatedPageContribution,
            isLarge: characterCount > 800,
            isMedium: characterCount > 400 && characterCount <= 800,
            isSmall: characterCount <= 400
        };
    };
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
    const [availableAccounts] = useState<CalendarAccount[]>([
        {
            id: 'demo-google',
            provider: CalendarProvider.GOOGLE,
            email: 'user@example.com',
            displayName: 'Google Calendar',
            isConnected: true
        },
        {
            id: 'demo-outlook',
            provider: CalendarProvider.OUTLOOK,
            email: 'user@company.com',
            displayName: 'Outlook Calendar',
            isConnected: true
        }
    ]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    // Load conversation data when dialog opens
    useEffect(() => {
        if (open && state.currentConversation?.sessionId) {
            loadConversationData();
        }
    }, [open, state.currentConversation?.sessionId]);

    const loadConversationData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // First try to use current messages if available
            let messages = state.currentConversation?.messages || [];
            
            // If no messages or need fresh data, fetch from API
            if (messages.length === 0 && state.currentConversation?.sessionId) {
                await getConversationBySessionId(state.currentConversation.sessionId);
                // After fetching, get updated messages from state
                messages = state.currentConversation?.messages || [];
            }

            // Process messages to extract agent responses and sections
            const processedResponses = processMessagesToAgentResponses(messages);
            setAgentResponses(processedResponses);
            
        } catch (err) {
            console.error('Error loading conversation data:', err);
            setError('Failed to load conversation data');
        } finally {
            setLoading(false);
        }
    };

    const processMessagesToAgentResponses = (messages: any[]): AgentResponse[] => {
        const agentMap = new Map<string, AgentSection[]>();

        // Filter for assistant messages (agent responses)
        const agentMessages = messages.filter(msg => 
            msg.authorRole?.label === 'assistant' && msg.content
        );

        agentMessages.forEach(message => {
            // Use agentName from backend Messenger field, or fallback to Unknown Agent
            const agentName = message.agentName || 'Unknown Agent';
            const content = message.content;

            // Extract sections starting with "###"
            const sections = extractSectionsFromContent(content);
            
            if (sections.length > 0) {
                if (agentMap.has(agentName)) {
                    agentMap.get(agentName)?.push(...sections);
                } else {
                    agentMap.set(agentName, sections);
                }
            }
        });

        // Convert map to array format
        return Array.from(agentMap.entries()).map(([agentName, sections]) => ({
            agentName,
            sections,
            expanded: false
        }));
    };

    // Simple markdown stripping function for display purposes only
    const stripMarkdown = (text: string): string => {
        let result = text;
        
        // Remove headers (including any amount of # and optional spaces)
        result = result.replace(/#{1,6}\s*/g, '');
        
        // Remove bold/italic (more comprehensive patterns, multiple passes)
        // Handle nested and complex formatting with more aggressive patterns
        let iterations = 0;
        do {
            const before = result;
            result = result
                // Handle bold patterns first (most specific to least specific)
                .replace(/\*\*\*(.+?)\*\*\*/g, '$1')     // Bold italic ***text***
                .replace(/\*\*(.+?)\*\*/g, '$1')        // Bold **text**
                .replace(/\*([^*\n]*?)\*/g, '$1')       // Italic *text*
                .replace(/___(.+?)___/g, '$1')          // Bold italic ___text___
                .replace(/__(.+?)__/g, '$1')            // Bold __text__
                .replace(/_([^_\n]*?)_/g, '$1')         // Italic _text_
                // Handle any remaining asterisks that might be leftover
                .replace(/^\*+\s*/gm, '')               // Remove leading asterisks
                .replace(/\s*\*+$/gm, '')               // Remove trailing asterisks
                .replace(/\*+/g, '');                   // Remove any remaining asterisks
            
            iterations++;
            // Keep processing until no more changes or max iterations reached
            if (before === result || iterations > 10) break;
        } while (true);
        
        // Remove links but keep text
        result = result.replace(/\[([^\]]+?)\]\([^)]*\)/g, '$1');
        
        // Remove inline code
        result = result.replace(/`([^`]+?)`/g, '$1');
        
        // Remove strikethrough
        result = result.replace(/~~([^~]+?)~~/g, '$1');
        
        // Remove horizontal rules (--- or ___ or ***)
        result = result.replace(/^[-_*]{3,}\s*$/gm, '');
        result = result.replace(/\s*[-_*]{3,}\s*/g, ' ');
        
        // Final cleanup: remove any remaining markdown characters
        result = result.replace(/[*_`~#]+/g, '');
        
        // Clean up extra whitespace and normalize
        result = result.replace(/\s+/g, ' ').trim();
        
        return result;
    };

    const extractSectionsFromContent = (content: string): AgentSection[] => {
        const sections: AgentSection[] = [];
        const lines = content.split('\n');
        let currentSection: CurrentSection | null = null;

        const addCurrentSection = () => {
            if (currentSection) {
                const processedSection = processSection({
                    title: currentSection.title,
                    content: currentSection.content.trim(),
                    selected: false
                });
                sections.push(processedSection);
            }
        };

        lines.forEach(line => {
            if (line.trim().startsWith('###')) {
                // Save previous section if exists
                addCurrentSection();
                
                // Start new section - strip markdown from title for DISPLAY only
                currentSection = {
                    title: stripMarkdown(line.trim().replace(/^###\s*/, '')),
                    content: ''
                };
            } else if (currentSection) {
                currentSection.content += line + '\n';
            }
        });

        // Don't forget the last section
        addCurrentSection();

        return sections;
    };

    // Function to detect and process lists in section content
    const processSection = (section: AgentSection): AgentSection => {
        const listItems: ListItem[] = [];
        let processedContent = section.content;
        let hasLists = false;

        // Split content into lines for processing
        const lines = section.content.split('\n');
        let subsectionMap: Record<string, { listItems: string[], paragraphs: string[] }> = {};

        // First pass: Analyze the structure to identify lists and their context
        const contentStructure: Array<{
            type: 'text' | 'list' | 'empty';
            content: string;
            lineIndex: number;
        }> = [];

        lines.forEach((line, index) => {
            const trimmed = line.trim();
            if (!trimmed) {
                contentStructure.push({ type: 'empty', content: '', lineIndex: index });
            } else if (trimmed.match(/^\s*[-*•]\s+/) || trimmed.match(/^\s*\d+\.\s+/)) {
                contentStructure.push({ type: 'list', content: trimmed, lineIndex: index });
                hasLists = true;
            } else {
                contentStructure.push({ type: 'text', content: trimmed, lineIndex: index });
            }
        });

        // Second pass: Identify subsection headers based on structural patterns
        let currentSubsection = '';
        
        for (let i = 0; i < contentStructure.length; i++) {
            const current = contentStructure[i];
            
            if (current.type === 'empty') continue;
            
            if (current.type === 'text') {
                // Look ahead to see if this text is followed by list items
                const nextNonEmptyIndex = contentStructure.findIndex((item, idx) => 
                    idx > i && item.type !== 'empty'
                );
                
                if (nextNonEmptyIndex !== -1 && 
                    contentStructure[nextNonEmptyIndex].type === 'list') {
                    // This text is followed by a list, so it's likely a subsection header
                    currentSubsection = current.content;
                    subsectionMap[currentSubsection] = { listItems: [], paragraphs: [] };
                    
                } else if (currentSubsection && subsectionMap[currentSubsection]) {
                    // This text belongs to the current subsection as paragraph content
                    subsectionMap[currentSubsection].paragraphs.push(current.content);
                    
                } else {
                    // Standalone text without clear subsection context
                    // Check if it looks like a header (short, not sentence-like)
                    if (current.content.length < 80 && 
                        !current.content.endsWith('.') && 
                        current.content.match(/^[A-Z]/) &&
                        current.content.split(' ').length <= 8) {
                        // Treat as potential subsection header
                        currentSubsection = current.content;
                        subsectionMap[currentSubsection] = { listItems: [], paragraphs: [] };
                        
                    }
                }
            } else if (current.type === 'list') {
                // Extract list item content
                const orderedMatch = current.content.match(/^\s*\d+\.\s+(.+)$/);
                const unorderedMatch = current.content.match(/^\s*[-*•]\s+(.+)$/);
                const rawItemContent = orderedMatch ? orderedMatch[1] : unorderedMatch![1];
                const itemContent = stripMarkdown(rawItemContent); // Strip markdown from list items for DISPLAY only
                
                
                if (currentSubsection && subsectionMap[currentSubsection]) {
                    subsectionMap[currentSubsection].listItems.push(itemContent);
                } else {
                    // List item without subsection context - treat as general item
                    listItems.push({
                        id: `item-${listItems.length}`,
                        content: itemContent,
                        selected: false, // Default to unchecked per new requirements
                        subsection: undefined
                    });
                }
            }
        }

        // Convert subsection map to list items (only for list items, paragraphs stay in processedContent)
        Object.entries(subsectionMap).forEach(([subsection, content]) => {
            content.listItems.forEach(itemContent => {
                listItems.push({
                    id: `item-${listItems.length}`,
                    content: itemContent,
                    selected: false, // Default to unchecked per new requirements
                    subsection: subsection
                });
            });
        });

        // Create processed content by removing list items but keeping subsection headers
        if (hasLists) {
            const contentLines = section.content.split('\n');
            const filteredLines = contentLines.filter(line => {
                const trimmed = line.trim();
                // Keep empty lines for formatting
                if (!trimmed) return true;
                
                // Remove list items
                if (trimmed.match(/^\s*[-*•]\s+/) || trimmed.match(/^\s*\d+\.\s+/)) {
                    return false;
                }
                
                // Keep all non-list content (this includes subsection headers and paragraphs)
                return true;
            });
            
            const rawProcessedContent = filteredLines.join('\n').replace(/\n\s*\n\s*\n/g, '\n\n').trim();
            processedContent = stripMarkdown(rawProcessedContent); // Strip markdown from processed content for DISPLAY only
        } else {
            processedContent = stripMarkdown(section.content); // Strip markdown from regular content for DISPLAY only
        }

        return {
            ...section,
            hasLists,
            listItems: hasLists ? listItems : undefined,
            processedContent: hasLists ? processedContent : undefined
        };
    };

    const handleSectionSelection = (agentIndex: number, sectionIndex: number) => {
        setAgentResponses(prev => 
            prev.map((agent, aIdx) => 
                aIdx === agentIndex
                    ? {
                        ...agent,
                        sections: agent.sections.map((section, sIdx) => {
                            if (sIdx === sectionIndex) {
                                const newSelected = !section.selected;
                                
                                // Keep current list item selections when toggling section
                                // This allows users to maintain their list selections
                                return {
                                    ...section,
                                    selected: newSelected
                                };
                            }
                            return section;
                        })
                    }
                    : agent
            )
        );
    };

    const handleSelectAll = (agentIndex: number, selectAll: boolean) => {
        setAgentResponses(prev => 
            prev.map((agent, aIdx) => 
                aIdx === agentIndex
                    ? {
                        ...agent,
                        sections: agent.sections.map(section => ({ 
                            ...section, 
                            selected: selectAll,
                            listItems: section.listItems?.map(item => ({
                                ...item,
                                // When selecting all, select all list items; when deselecting, unselect all
                                selected: selectAll
                            }))
                        }))
                    }
                    : agent
            )
        );
    };

    const handleListItemSelection = (agentIndex: number, sectionIndex: number, itemId: string) => {
        setAgentResponses(prev => 
            prev.map((agent, aIdx) => 
                aIdx === agentIndex
                    ? {
                        ...agent,
                        sections: agent.sections.map((section, sIdx) => {
                            if (sIdx === sectionIndex) {
                                const updatedListItems = section.listItems?.map(item =>
                                    item.id === itemId
                                        ? { ...item, selected: !item.selected }
                                        : item
                                );
                                
                                // Check if any list item is now selected
                                const anyListItemSelected = updatedListItems?.some(item => item.selected) || false;
                                
                                // Auto-check the section if a list item was selected and section wasn't checked
                                const shouldAutoCheckSection = anyListItemSelected && !section.selected;
                                
                                return {
                                    ...section,
                                    listItems: updatedListItems,
                                    selected: shouldAutoCheckSection ? true : section.selected
                                };
                            }
                            return section;
                        })
                    }
                    : agent
            )
        );
    };

    const getSelectedSections = () => {
        const selected: { 
            agentName: string; 
            title: string; 
            content: string; 
            hasSubsections?: boolean;
            subsections?: { title: string; listItems: string[] }[];
        }[] = [];
        
        agentResponses.forEach(agent => {
            agent.sections.forEach(section => {
                if (section.selected) {
                    // For sections with lists, require at least one list item to be selected
                    if (section.hasLists && section.listItems) {
                        const selectedItems = section.listItems.filter(item => item.selected);
                        if (selectedItems.length === 0) {
                            // Skip this section - it's invalid for export
                            return;
                        }
                    }
                    
                    let content = section.content;
                    let hasSubsections = false;
                    let subsections: { title: string; listItems: string[] }[] = [];
                    
                    // If section has lists, handle mixed content scenarios with structured data
                    if (section.hasLists && section.listItems && section.processedContent !== undefined) {
                        const selectedItems = section.listItems.filter(item => item.selected);
                        const hasTextContent = section.processedContent.trim().length > 0;
                        
                        // Check if we have subsections
                        const grouped = selectedItems.reduce((acc, item) => {
                            const key = item.subsection || 'general';
                            if (!acc[key]) acc[key] = [];
                            acc[key].push(item);
                            return acc;
                        }, {} as Record<string, typeof selectedItems>);
                        
                        const hasSubsectionStructure = Object.keys(grouped).some(key => key !== 'general');
                        
                        if (hasSubsectionStructure && selectedItems.length > 0) {
                            // Use structured subsection data
                            hasSubsections = true;
                            content = hasTextContent ? stripMarkdown(section.processedContent.trim()) : stripMarkdown(section.content);
                            
                            // Build subsections array
                            Object.entries(grouped).forEach(([subsectionKey, items]) => {
                                if (subsectionKey !== 'general') {
                                    subsections.push({
                                        title: subsectionKey,
                                        listItems: items.map(item => item.content)
                                    });
                                } else if (items.length > 0) {
                                    // General items without subsection
                                    subsections.push({
                                        title: 'Items',
                                        listItems: items.map(item => item.content)
                                    });
                                }
                            });
                        } else {
                            // Fallback to text reconstruction for simple cases
                            if (hasTextContent && selectedItems.length > 0) {
                                const selectedListContent = selectedItems.map(item => `• ${item.content}`).join('\n');
                                content = stripMarkdown(section.processedContent.trim() + '\n\n' + selectedListContent);
                            } else if (hasTextContent && selectedItems.length === 0) {
                                content = stripMarkdown(section.processedContent.trim());
                            } else if (!hasTextContent && selectedItems.length > 0) {
                                content = selectedItems.map(item => `• ${item.content}`).join('\n');
                            } else {
                                content = stripMarkdown(section.content);
                            }
                        }
                    } else {
                        // If no lists, strip markdown from original content
                        content = stripMarkdown(section.content);
                    }
                    
                    // Update section title to reflect selected count when applicable
                    let updatedTitle = section.title;
                    if (section.hasLists && section.listItems) {
                        const selectedCount = section.listItems.filter(item => item.selected).length;
                        const totalCount = section.listItems.length;
                        
                        // Only update title if user selected fewer items than total
                        if (selectedCount < totalCount && selectedCount > 0) {
                            // Look for numbers in the title and replace with selected count
                            // This regex looks for standalone numbers (not part of larger numbers or dates)
                            const numberRegex = /\b(\d+)\b/;
                            const match = updatedTitle.match(numberRegex);
                            
                            if (match) {
                                const originalNumber = parseInt(match[1]);
                                // Only replace if the original number matches or is close to total count
                                // This prevents replacing unrelated numbers like years, versions, etc.
                                if (originalNumber === totalCount || Math.abs(originalNumber - totalCount) <= 2) {
                                    updatedTitle = updatedTitle.replace(numberRegex, selectedCount.toString());
                                }
                            }
                        }
                    }
                    
                    selected.push({
                        agentName: agent.agentName,
                        title: updatedTitle,
                        content: content,
                        hasSubsections: hasSubsections,
                        subsections: hasSubsections ? subsections : undefined
                    });
                }
            });
        });

        return selected;
    };

    const convertSectionsToMessageContent = (): MessageContent[] => {
        return getSelectedSections().map((section, index) => ({
            id: `section-${index}`,
            type: 'text' as const,
            content: section.content,
            title: `${section.agentName}: ${section.title}`,
            selected: true,
            metadata: {
                agentName: section.agentName,
                sectionTitle: section.title
            }
        }));
    };

    const handleCalendarEventCreated = (event: CalendarEvent, account: CalendarAccount) => {
        setCalendarDialogOpen(false);
        // TODO: Show success message or handle post-creation logic
    };

    const handleOpenCalendarDialog = () => {
        const validSections = getSelectedSections();
        if (validSections.length === 0) {
            // Check if there are sections selected but invalid
            const selectedButInvalidSections = agentResponses.flatMap(agent => 
                agent.sections.filter(section => {
                    if (!section.selected) return false;
                    if (section.hasLists && section.listItems) {
                        return !section.listItems.some(item => item.selected);
                    }
                    return false;
                })
            );
            
            if (selectedButInvalidSections.length > 0) {
                setError(`Please select at least one item in each section with lists. ${selectedButInvalidSections.length} section(s) need list items selected.`);
            } else {
                setError('Please select at least one section to add to calendar');
            }
            return;
        }
        setCalendarDialogOpen(true);
    };

    const handleExportToPdf = async () => {
        const selectedSections = getSelectedSections();
        if (selectedSections.length === 0) {
            // Check if there are sections selected but invalid (no list items selected)
            const selectedButInvalidSections = agentResponses.flatMap(agent => 
                agent.sections.filter(section => {
                    if (!section.selected) return false;
                    if (section.hasLists && section.listItems) {
                        return !section.listItems.some(item => item.selected);
                    }
                    return false;
                })
            );
            
            if (selectedButInvalidSections.length > 0) {
                setError(`Please select at least one item in each section with lists. ${selectedButInvalidSections.length} section(s) need list items selected.`);
            } else {
                setError('Please select at least one section to export');
            }
            return;
        }

        // Check content limits using backend-calculated metrics (only if single page restriction is enabled)
        if (singlePageRestriction && contentMetrics && contentMetrics.isOverLimit) {
            if (!allowTruncation) {
                // Prevent export if truncation is not allowed and content exceeds limits
                setError(
                    `Content exceeds single page limits (${contentMetrics.totalCharacters} characters, estimated ${contentMetrics.estimatedPages} pages). ` +
                    `Either enable "Allow Truncation" or disable "Single Page Restriction" to export this content.`
                );
                return;
            } else {
                // Warn user about truncation if it's allowed
                const confirmed = window.confirm(
                    `Content exceeds single page limits (${contentMetrics.totalCharacters} characters, estimated ${contentMetrics.estimatedPages} pages). ` +
                    `The PDF will be automatically truncated to fit one page. Continue?`
                );
                if (!confirmed) {
                    setError('Export cancelled due to content size limits');
                    return;
                }
            }
        }

        const sessionId = state.currentConversation?.sessionId;
        if (!sessionId) {
            setError('No active session found');
            return;
        }

        try {
            setError('');
            setIsExporting(true);
            // Use the updated exportBattleCard method that supports options
            await exportBattleCard(
                selectedSections,
                sessionId,
                `Battle Card - ${new Date().toLocaleDateString()}`,
                new AbortController().signal,
                {
                    singlePageRestriction,
                    allowTruncation
                }
            );
            
        } catch (err) {
            setError('Failed to export to PDF. Please try again.');
        } finally {
            setIsExporting(false);
        }
    };

    const handleAddToCalendar = () => {
        const validSections = getSelectedSections();
        if (validSections.length === 0) {
            // Check if there are sections selected but invalid
            const selectedButInvalidSections = agentResponses.flatMap(agent => 
                agent.sections.filter(section => {
                    if (!section.selected) return false;
                    if (section.hasLists && section.listItems) {
                        return !section.listItems.some(item => item.selected);
                    }
                    return false;
                })
            );
            
            if (selectedButInvalidSections.length > 0) {
                setError(`Please select at least one item in each section with lists. ${selectedButInvalidSections.length} section(s) need list items selected.`);
            } else {
                setError('Please select at least one section to add to calendar');
            }
            return;
        }

        // Switch to calendar tab and show the integration
        setCurrentTab(1);
    };

    const getTotalSelectedCount = () => {
        return agentResponses.reduce((total, agent) => 
            total + agent.sections.filter(section => section.selected).length, 0
        );
    };

    const getValidSelectedCount = () => {
        return getSelectedSections().length;
    };

    // Helper function to determine section checkbox state for visual feedback
    const getSectionCheckboxState = (section: AgentSection) => {
        if (!section.selected) return { checked: false, indeterminate: false, isValid: true };
        
        if (section.hasLists && section.listItems) {
            const hasSelectedItems = section.listItems.some(item => item.selected);
            return {
                checked: section.selected,
                indeterminate: section.selected && !hasSelectedItems,
                isValid: hasSelectedItems
            };
        }
        
        return { checked: true, indeterminate: false, isValid: true };
    };

    // Content Metrics Display Component
    const ContentMetricsDisplay: React.FC = () => {
        // Show disabled message when single page restriction is off
        if (!singlePageRestriction) {
            return (
                <Box sx={{ 
                    mt: 1, 
                    p: 1, 
                    backgroundColor: 'rgba(0,0,0,0.02)', 
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    cursor: 'pointer'
                }}
                onClick={() => setContentAnalysisExpanded(!contentAnalysisExpanded)}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                            Content Analysis (Disabled)
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#4caf50' }}>
                            Unlimited Pages ∞
                        </Typography>
                    </Box>
                    {contentAnalysisExpanded && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Single page restriction is disabled. Content can span multiple pages without limits.
                        </Typography>
                    )}
                </Box>
            );
        }

        if (metricsLoading) {
            return (
                <Box sx={{ 
                    mt: 1, 
                    p: 1, 
                    backgroundColor: 'rgba(0,0,0,0.02)', 
                    borderRadius: 1,
                    border: '1px solid #e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption">
                        Analyzing content...
                    </Typography>
                </Box>
            );
        }

        if (!contentMetrics) return null;

        const getStatusIcon = () => {
            if (contentMetrics.isOverLimit) return '❌';
            if (contentMetrics.isNearLimit) return '⚠️';
            return '✅';
        };

        const getStatusText = () => {
            if (contentMetrics.isOverLimit) return 'Exceeds Page Limit';
            if (contentMetrics.isNearLimit) return 'Near Page Limit';
            return 'Fits Well';
        };

        const getStatusColor = () => {
            if (contentMetrics.isOverLimit) return '#f44336';
            if (contentMetrics.isNearLimit) return '#ff9800';
            return '#4caf50';
        };
        
        return (
            <Box sx={{ 
                mt: 1, 
                p: 1, 
                backgroundColor: 'rgba(0,0,0,0.02)', 
                borderRadius: 1,
                border: contentMetrics.isOverLimit ? '1px solid #f44336' : '1px solid #e0e0e0',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)',
                }
            }}
            onClick={() => setContentAnalysisExpanded(!contentAnalysisExpanded)}
            >
                {/* Collapsed View */}
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            Content Analysis
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                            ({contentMetrics.totalCharacters} chars, {contentMetrics.sectionCount} sections)
                        </Typography>
                    </Box>
                    
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                fontWeight: 'bold',
                                color: getStatusColor(),
                                fontSize: '0.75rem'
                            }}
                        >
                            {getStatusIcon()} {getStatusText()}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                            {contentAnalysisExpanded ? '▲' : '▼'}
                        </Typography>
                    </Box>
                </Box>

                {/* Expanded View */}
                {contentAnalysisExpanded && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
                        {/* Page Estimation */}
                        <Box sx={{ mb: 1.5 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    Estimated Pages: {contentMetrics.estimatedPages} / 1
                                </Typography>
                                <Typography variant="caption" sx={{ color: getUsageColor(contentMetrics.pageUsagePercent) }}>
                                    {contentMetrics.pageUsagePercent.toFixed(0)}%
                                </Typography>
                            </Box>
                            <Box sx={{ 
                                width: '100%', 
                                height: 6, 
                                backgroundColor: 'rgba(0,0,0,0.1)', 
                                borderRadius: 3,
                                overflow: 'hidden'
                            }}>
                                <Box sx={{
                                    width: `${Math.min(contentMetrics.pageUsagePercent, 100)}%`,
                                    height: '100%',
                                    backgroundColor: getUsageColor(contentMetrics.pageUsagePercent),
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease'
                                }} />
                            </Box>
                        </Box>
                        
                        {/* Character Count */}
                        <Box sx={{ mb: 1.5 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                    Content: {contentMetrics.totalCharacters.toLocaleString()} / {contentMetrics.limits.maxCharacters.toLocaleString()} chars
                                </Typography>
                                <Typography variant="caption" sx={{ color: getUsageColor(contentMetrics.characterUsagePercent) }}>
                                    {contentMetrics.characterUsagePercent.toFixed(0)}%
                                </Typography>
                            </Box>
                            <Box sx={{ 
                                width: '100%', 
                                height: 6, 
                                backgroundColor: 'rgba(0,0,0,0.1)', 
                                borderRadius: 3,
                                overflow: 'hidden'
                            }}>
                                <Box sx={{
                                    width: `${Math.min(contentMetrics.characterUsagePercent, 100)}%`,
                                    height: '100%',
                                    backgroundColor: getUsageColor(contentMetrics.characterUsagePercent),
                                    borderRadius: 3,
                                    transition: 'all 0.3s ease'
                                }} />
                            </Box>
                        </Box>
                        
                        {/* Summary Stats */}
                        <Box display="flex" gap={2} sx={{ mt: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">
                                {contentMetrics.sectionCount} sections
                            </Typography>
                            {contentMetrics.agentCount && (
                                <Typography variant="caption" color="text.secondary">
                                    {contentMetrics.agentCount} agents
                                </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary">
                                ~{Math.round(contentMetrics.estimatedHeight || 0)} pts
                            </Typography>
                            {/* Add list item statistics */}
                            <Typography variant="caption" color="text.secondary">
                                {getListItemStats()}
                            </Typography>
                        </Box>
                        
                        {/* Backend Recommendations */}
                        {contentMetrics.recommendations && contentMetrics.recommendations.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                                {contentMetrics.recommendations.map((rec: string, index: number) => (
                                    <Alert 
                                        key={index}
                                        severity={contentMetrics.isOverLimit ? "error" : contentMetrics.isNearLimit ? "warning" : "info"} 
                                        sx={{ mt: 0.5, py: 0.5 }}
                                    >
                                        <Typography variant="caption">
                                            {rec}
                                        </Typography>
                                    </Alert>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isMobile} // Use state for responsive fullscreen
            PaperProps={{
                sx: {
                    height: { xs: '100vh', md: '80vh' }, // Full height on mobile, 80vh on desktop
                    maxHeight: { xs: '100vh', md: '80vh' },
                    margin: { xs: 0, md: 'auto' }, // No margin on mobile
                    borderRadius: { xs: 0, md: 2 }, // No border radius on mobile
                }
            }}
        >
            <DialogTitle sx={{ 
                pb: 0.5, 
                pt: { xs: 2, md: 1.5 }, // More top padding on mobile
                px: { xs: 2, md: 3 } // Horizontal padding for mobile
            }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.25rem', md: '1.05rem' } }}>
                        Generate Battle Card
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.85rem', md: '0.75rem' } }}>
                            {getValidSelectedCount()} of {getTotalSelectedCount()} sections ready
                        </Typography>
                        {getTotalSelectedCount() > getValidSelectedCount() && (
                            <Typography variant="caption" sx={{ color: '#ff9800', fontSize: { xs: '0.8rem', md: '0.7rem' } }}>
                                ⚠️ {getTotalSelectedCount() - getValidSelectedCount()} need items
                            </Typography>
                        )}
                    </Box>
                </Box>
                
                {/* Content Control Toggles */}
                <Box display="flex" gap={2} sx={{ mb: 0.5, justifyContent: 'center' }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={singlePageRestriction}
                                onChange={(e) => setSinglePageRestriction(e.target.checked)}
                                color="primary"
                                size="small"
                            />
                        }
                        label={
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                Single Page Restriction
                            </Typography>
                        }
                        sx={{ margin: 0 }}
                    />
                </Box>
                
                {/* Tabs Navigation */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                        value={currentTab} 
                        onChange={handleTabChange}
                        sx={{
                            minHeight: 'auto',
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                                minHeight: 32,
                                py: 0.25,
                                fontSize: '0.85rem',
                            },
                            '& .MuiTab-root.Mui-selected': {
                                color: '#e50c20',
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#e50c20',
                            },
                        }}
                    >
                        <Tab 
                            icon={<ChecklistIcon />} 
                            label="Select Sections" 
                            iconPosition="start"
                            sx={{ gap: 1 }}
                        />
                        {CALENDAR_ENABLED && (
                            <Tab 
                                icon={<EventIcon />} 
                                label="Schedule Meeting" 
                                iconPosition="start"
                                sx={{ gap: 1 }}
                                disabled={getValidSelectedCount() === 0}
                            />
                        )}
                    </Tabs>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ 
                p: 0, 
                height: { xs: 'calc(100vh - 180px)', md: 'calc(80vh - 130px)' }, // Adjust for mobile vs desktop
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden' // Prevent main content scrolling
            }}>
                <TabPanel value={currentTab} index={0}>
                    {/* Battle Card Selection Content */}
                    {loading && (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ m: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {!loading && agentResponses.length === 0 && (
                        <Alert severity="info" sx={{ m: 2 }}>
                            No agent responses with sections found in this conversation.
                        </Alert>
                    )}

                    {!loading && agentResponses.length > 0 && (
                        <Box sx={{ 
                            px: 2, 
                            height: '100%', 
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            gap: 1
                        }}>
                            {/* Agent Summary Bar - Desktop Only */}
                            <Box sx={{ 
                                display: { xs: 'none', sm: 'flex' }, 
                                gap: 1, 
                                flexWrap: 'wrap',
                                py: 1,
                                borderBottom: '1px solid #e0e0e0',
                                flexShrink: 0
                            }}>
                                {agentResponses.map((agent, index) => {
                                    const selectedCount = agent.sections.filter(s => s.selected).length;
                                    const validCount = agent.sections.filter(section => {
                                        if (!section.selected) return false;
                                        if (section.hasLists && section.listItems) {
                                            return section.listItems.some(item => item.selected);
                                        }
                                        return true;
                                    }).length;
                                    
                                    return (
                                        <Chip
                                            key={index}
                                            label={`${agent.agentName}: ${validCount}/${agent.sections.length}`}
                                            variant={index === selectedAgentIndex ? "filled" : "outlined"}
                                            color={index === selectedAgentIndex ? "primary" : "default"}
                                            size="small"
                                            clickable
                                            onClick={() => setSelectedAgentIndex(index)}
                                            sx={{
                                                bgcolor: index === selectedAgentIndex ? '#e50c20' : 'transparent',
                                                color: index === selectedAgentIndex ? '#fff' : 'text.primary',
                                                borderColor: selectedCount > 0 ? '#e50c20' : '#e0e0e0',
                                                '&:hover': {
                                                    bgcolor: index === selectedAgentIndex ? '#b8091a' : 'rgba(229, 12, 32, 0.1)',
                                                },
                                                fontSize: '0.75rem',
                                                height: 28,
                                                // Show warning indicator if selected but invalid sections
                                                ...(selectedCount > validCount && {
                                                    '&::after': {
                                                        content: '"⚠️"',
                                                        marginLeft: '4px',
                                                        fontSize: '0.7rem'
                                                    }
                                                })
                                            }}
                                        />
                                    );
                                })}
                            </Box>

                            {/* Agent Dropdown - Mobile Friendly Alternative */}
                            <Box sx={{ 
                                display: { xs: 'block', sm: 'none' }, 
                                mb: 1, 
                                marginTop:"5px",
                                flexShrink: 0 
                            }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Select Agent</InputLabel>
                                    <Select
                                        value={selectedAgentIndex}
                                        label="Select Agent"
                                        onChange={(e) => setSelectedAgentIndex(e.target.value as number)}
                                        sx={{
                                            '& .MuiSelect-select': {
                                                py: 1,
                                            }
                                        }}
                                    >
                                        {agentResponses.map((agent, index) => {
                                            const selectedCount = agent.sections.filter(s => s.selected).length;
                                            const validCount = agent.sections.filter(section => {
                                                if (!section.selected) return false;
                                                if (section.hasLists && section.listItems) {
                                                    return section.listItems.some(item => item.selected);
                                                }
                                                return true;
                                            }).length;
                                            
                                            return (
                                                <MenuItem key={index} value={index}>
                                                    <Box display="flex" justifyContent="space-between" width="100%">
                                                        <Typography>{agent.agentName}</Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {validCount}/{agent.sections.length}
                                                            {selectedCount > validCount && ' ⚠️'}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Current Agent Header with Controls */}
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                flexShrink: 0,
                                py: 1
                            }}>
                                <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                                    {agentResponses[selectedAgentIndex]?.agentName}
                                </Typography>
                                
                                <Box display="flex" gap={1}>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => handleSelectAll(selectedAgentIndex, true)}
                                        sx={{ 
                                            minWidth: 'auto',
                                            fontSize: '0.75rem',
                                            px: 1,
                                            py: 0.5,
                                            textTransform: 'none'
                                        }}
                                    >
                                        Select All
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => handleSelectAll(selectedAgentIndex, false)}
                                        sx={{ 
                                            minWidth: 'auto',
                                            fontSize: '0.75rem',
                                            px: 1,
                                            py: 0.5,
                                            textTransform: 'none'
                                        }}
                                    >
                                        Select None
                                    </Button>
                                </Box>
                            </Box>

                            {/* Section Cards - Full Available Space */}
                            <Box
                                sx={{
                                    flex: 1, // Take all remaining space
                                    height: '100%', // Use full available height
                                    maxHeight: { xs: 'calc(100vh - 300px)', md: 'calc(80vh - 300px)' }, // More space on mobile, appropriate for desktop
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1, // Reduced gap
                                    alignContent: 'flex-start', // Align cards to top
                                    pb: { xs: 12, sm: 8 }, // Increased bottom padding to clear content analysis drawer
                                    // Custom scrollbar styling
                                    '&::-webkit-scrollbar': {
                                        width: 8,
                                        backgroundColor: 'transparent',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        backgroundColor: 'rgba(0,0,0,0.05)',
                                        borderRadius: 6,
                                        margin: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        backgroundColor: '#e50c20',
                                        borderRadius: 6,
                                        border: '1px solid rgba(229, 12, 32, 0.3)',
                                        '&:hover': {
                                            backgroundColor: '#b8091a',
                                            border: '1px solid rgba(184, 9, 26, 0.5)',
                                        },
                                        '&:active': {
                                            backgroundColor: '#8a0613',
                                        },
                                    },
                                    '&::-webkit-scrollbar-corner': {
                                        backgroundColor: 'transparent',
                                    },
                                    // For Firefox
                                    scrollbarWidth: 'thin',
                                    scrollbarColor: '#e50c20 rgba(0,0,0,0.05)',
                                }}
                            >
                                {agentResponses[selectedAgentIndex]?.sections.map((section, sectionIndex) => (
                                    <Box
                                        key={sectionIndex}
                                        sx={{
                                            width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.333% - 12px)', lg: 'calc(25% - 12px)' }, // Smaller responsive grid
                                            minWidth: { xs: 'auto', sm: 240, md: 260 }, // Reduced minimum width
                                            maxWidth: { xs: '100%', sm: 320 }, // Added max width
                                            flexShrink: 0,
                                            border: section.selected ? '2px solid #e50c20' : '1px solid #e0e0e0',
                                            borderRadius: 1.5, // Slightly smaller border radius
                                            backgroundColor: 'background.paper',
                                            transition: 'all 0.2s ease',
                                            cursor: 'pointer',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                borderColor: section.selected ? '#e50c20' : '#ccc',
                                                transform: { xs: 'none', sm: 'translateY(-1px)' }, // Reduced hover transform
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)', // Smaller shadow
                                            },
                                        }}
                                        onClick={() => handleSectionSelection(selectedAgentIndex, sectionIndex)}
                                    >
                                        {/* Main section content area - this gets highlighted */}
                                        <Box
                                            sx={{
                                                p: 1, // Reduced padding
                                                backgroundColor: section.selected ? 'rgba(229, 12, 32, 0.05)' : 'transparent',
                                                borderRadius: '6px 6px 0 0', // Smaller border radius
                                                transition: 'background-color 0.2s',
                                                '&:hover': {
                                                    backgroundColor: section.selected ? 'rgba(229, 12, 32, 0.1)' : 'rgba(0,0,0,0.02)',
                                                },
                                            }}
                                        >
                                            <Box display="flex" alignItems="flex-start" gap={0.75} mb={1}> {/* Reduced gap and margin */}
                                                {(() => {
                                                    const checkboxState = getSectionCheckboxState(section);
                                                    return (
                                                        <Checkbox
                                                            checked={checkboxState.checked}
                                                            indeterminate={checkboxState.indeterminate}
                                                            onChange={() => handleSectionSelection(selectedAgentIndex, sectionIndex)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            size="small"
                                                            sx={{
                                                                p: 0,
                                                                '&.Mui-checked': {
                                                                    color: checkboxState.isValid ? '#e50c20' : '#ff9800',
                                                                },
                                                                '&.MuiCheckbox-indeterminate': {
                                                                    color: '#ff9800',
                                                                },
                                                            }}
                                                        />
                                                    );
                                                })()}
                                                <Typography
                                                    variant="subtitle2"
                                                    fontWeight="bold"
                                                    sx={{
                                                        flexGrow: 1,
                                                        fontSize: '0.8rem',
                                                        lineHeight: 1.4, // Increased from 1.2
                                                        color: section.selected ? '#e50c20' : 'text.primary',
                                                    }}
                                                >
                                                    {section.title}
                                                    {section.hasLists && (
                                                        <Typography component="span" sx={{ ml: 0.5, fontSize: '0.65rem', color: 'text.secondary' }}>
                                                            ({section.listItems?.filter(item => item.selected).length || 0}/{section.listItems?.length} items)
                                                        </Typography>
                                                    )}
                                                </Typography>
                                            </Box>
                                            
                                            {/* Show processed content (text without list items) */}
                                            {section.processedContent && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        color: 'text.secondary',
                                                        lineHeight: 1.4, // Increased from 1.25
                                                        // Show full content when selected, truncated when not
                                                        ...(section.selected ? {
                                                            // Full content display
                                                            mb: 0.5,
                                                        } : {
                                                            // Truncated preview
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            mb: 0.75,
                                                        })
                                                    }}
                                                >
                                                    {section.selected 
                                                        ? section.processedContent
                                                        : `${section.processedContent.substring(0, 80)}${section.processedContent.length > 80 ? '...' : ''}`
                                                    }
                                                </Typography>
                                            )}
                                            
                                            {/* Show original content if no lists */}
                                            {!section.hasLists && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: '0.75rem',
                                                        color: 'text.secondary',
                                                        lineHeight: 1.4, // Increased from 1.3
                                                        // Show full content when selected, truncated when not
                                                        ...(section.selected ? {
                                                            // Full content display
                                                        } : {
                                                            // Truncated preview
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 3,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        })
                                                    }}
                                                >
                                                    {section.selected 
                                                        ? section.content
                                                        : `${section.content.substring(0, 120)}${section.content.length > 120 ? '...' : ''}`
                                                    }
                                                </Typography>
                                            )}
                                        </Box>

                                        {/* List items section - always show for sections with lists */}
                                        {section.hasLists && section.listItems && (
                                            <Box sx={{ 
                                                px: 1.5, 
                                                pb: 1.5, 
                                                backgroundColor: section.selected ? 'rgba(229, 12, 32, 0.02)' : 'background.paper', 
                                                borderRadius: '0 0 6px 6px',
                                                borderTop: '1px solid #e0e0e0'
                                            }}>
                                                <Box sx={{ pt: 1.5 }}>
                                                    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                                                        <Typography variant="caption" fontWeight="bold" sx={{ color: '#e50c20', fontSize: '0.7rem' }}>
                                                            Select items:
                                                        </Typography>
                                                        {(() => {
                                                            const checkboxState = getSectionCheckboxState(section);
                                                            if (checkboxState.indeterminate) {
                                                                return (
                                                                    <Typography variant="caption" sx={{ color: '#ff9800', fontSize: '0.65rem' }}>
                                                                        ⚠️ Select one
                                                                    </Typography>
                                                                );
                                                            }
                                                            return null;
                                                        })()}
                                                    </Box>
                                                    <Box sx={{ maxHeight: '120px', overflowY: 'auto', overflowX: 'hidden', width: '100%' }}>
                                                        {/* Group items by subsection */}
                                                        {(() => {
                                                            // Group items by subsection
                                                            const grouped = section.listItems!.reduce((acc, item) => {
                                                                const key = item.subsection || 'general';
                                                                if (!acc[key]) acc[key] = [];
                                                                acc[key].push(item);
                                                                return acc;
                                                            }, {} as Record<string, ListItem[]>);

                                                            return Object.entries(grouped).map(([subsectionKey, items]) => (
                                                                <Box key={subsectionKey} sx={{ mb: 2 }}>
                                                                    {/* Show subsection header if it exists */}
                                                                    {subsectionKey !== 'general' && (
                                                                        <Typography 
                                                                            variant="caption" 
                                                                            sx={{ 
                                                                                fontWeight: 'bold', 
                                                                                color: 'text.primary',
                                                                                fontSize: '0.75rem',
                                                                                mb: 0.5,
                                                                                display: 'block',
                                                                                borderBottom: '1px solid #e0e0e0',
                                                                                pb: 0.5
                                                                            }}
                                                                        >
                                                                            {subsectionKey}
                                                                        </Typography>
                                                                    )}
                                                                    
                                                                    {/* Render items for this subsection */}
                                                                    {items.map((item) => (
                                                                        <Box 
                                                                            key={item.id}
                                                                            sx={{ 
                                                                                display: 'flex', 
                                                                                alignItems: 'flex-start', 
                                                                                gap: 0.75, 
                                                                                mb: 0.75,
                                                                                ml: subsectionKey !== 'general' ? 0.75 : 0, // Reduced indent
                                                                                p: 0.75, // Reduced padding
                                                                                borderRadius: 1,
                                                                                cursor: 'pointer',
                                                                                width: '100%',
                                                                                minWidth: 0,
                                                                                overflow: 'hidden',
                                                                            }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleListItemSelection(selectedAgentIndex, sectionIndex, item.id);
                                                                            }}
                                                                        >
                                                                            <Checkbox
                                                                                checked={item.selected}
                                                                                size="small"
                                                                                onChange={() => handleListItemSelection(selectedAgentIndex, sectionIndex, item.id)}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                sx={{
                                                                                    p: 0.5,
                                                                                    '&.Mui-checked': {
                                                                                        color: '#e50c20',
                                                                                    },
                                                                                }}
                                                                            />
                                                                            <Typography 
                                                                                variant="caption" 
                                                                                sx={{ 
                                                                                    fontSize: '0.7rem',
                                                                                    lineHeight: 1.3, // Increased from 1.25
                                                                                    color: 'text.secondary',
                                                                                    cursor: 'pointer',
                                                                                    wordBreak: 'break-word',
                                                                                    whiteSpace: 'pre-wrap',
                                                                                    overflow: 'hidden',
                                                                                    flexGrow: 1,
                                                                                }}
                                                                            >
                                                                                {stripMarkdown(item.content)}
                                                                            </Typography>
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            ));
                                                        })()
                                                        }
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </TabPanel>

                {CALENDAR_ENABLED && (
                    <TabPanel value={currentTab} index={1}>
                        {/* Calendar Content */}
                        <Box sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>
                                Schedule Meeting
                            </Typography>
                            <Typography variant="body2" color="textSecondary" mb={3}>
                                Selected {getTotalSelectedCount()} sections for your meeting agenda:
                            </Typography>
                            
                            {/* Display Selected Sections */}
                            <Box sx={{ mb: 3, maxHeight: '200px', overflowY: 'auto' }}>
                                {getSelectedSections().map((section, index) => (
                                    <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="bold" color="#e50c20">
                                            {section.agentName}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold">
                                            {section.title}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {section.content.substring(0, 100)}...
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                            
                            {/* Calendar Integration Button */}
                            <Box sx={{ textAlign: 'center' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        background: 'background.paper',
                                        borderRadius: '40px',
                                        boxShadow: 'none',
                                        height: '48px',
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        cursor: getTotalSelectedCount() === 0 ? 'not-allowed' : 'pointer',
                                        px: 0,
                                        py: 0,
                                        border: '1.5px solid #e50c20',
                                        transition: 'background 0.2s, color 0.2s',
                                        opacity: getTotalSelectedCount() === 0 ? 0.5 : 1,
                                        margin: '0 auto',
                                        width: 'fit-content',
                                        ...(getTotalSelectedCount() > 0 && {
                                            '&:hover': {
                                                bgcolor: '#e50c20',
                                                '& .calendar-btn-text': {
                                                    color: '#fff',
                                                },
                                            },
                                        }),
                                    }}
                                    onClick={() => {
                                        if (getTotalSelectedCount() > 0) handleOpenCalendarDialog();
                                    }}
                                >
                                    <Box
                                        sx={{
                                            background: '#e50c20',
                                            width: '2%',
                                            minWidth: '32px',
                                            height: '100%',
                                            borderRadius: '40px 0 0 40px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <EventIcon sx={{ fontSize: 18, color: '#fff' }} />
                                    </Box>
                                    <Typography
                                        className="calendar-btn-text"
                                        component="span"
                                        sx={{
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            pl: 2,
                                            pr: 3,
                                            py: 1.5,
                                            textAlign: 'left',
                                            whiteSpace: 'nowrap',
                                            color: 'text.primary',
                                            userSelect: 'none',
                                            transition: 'color 0.2s',
                                        }}
                                    >
                                        Create Calendar Event
                                    </Typography>
                                </Box>
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    This will open a calendar dialog to schedule a meeting with your selected sections as the agenda.
                                </Typography>
                            </Box>
                        </Box>
                    </TabPanel>
                )}

                {!CALENDAR_ENABLED && (
                    <TabPanel value={currentTab} index={1}>
                        {/* Coming Soon Message */}
                        <Box sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'rgba(229, 12, 32, 0.1)',
                                        borderRadius: '40px',
                                        height: '48px',
                                        px: 3,
                                        py: 0,
                                        border: '1.5px dashed #e50c20',
                                        margin: '0 auto',
                                        width: 'fit-content',
                                        opacity: 0.7,
                                    }}
                                >
                                    <EventIcon sx={{ fontSize: 18, color: '#e50c20', mr: 1 }} />
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: '1rem',
                                            fontWeight: 600,
                                            color: '#e50c20',
                                            userSelect: 'none',
                                        }}
                                    >
                                        Calendar Integration - Coming Soon
                                    </Typography>
                                </Box>
                                
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                    Calendar event creation feature is currently under development.
                                </Typography>
                            </Box>
                        </Box>
                    </TabPanel>
                )}
            </DialogContent>

            {/* Content Analysis Section - only show when single page restriction is enabled */}
            {singlePageRestriction && getTotalSelectedCount() > 0 && (
                <Box sx={{ px: 1.5, pb: 1 }}>
                    <ContentMetricsDisplay />
                </Box>
            )}

            <DialogActions sx={{ 
                p: 1.5,
                py: 1, 
                borderTop: '1px solid #e0e0e0', 
                gap: 1.5,
                flexDirection: 'row',
                alignItems: 'center',
                position: 'static',
                backgroundColor: 'background.paper',
                zIndex: 1
            }}>
                <Button 
                    onClick={onClose}
                    sx={{
                        borderRadius: '40px',
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 2.5,
                        py: 0.5,
                        fontSize: '0.9rem',
                        color: 'text.secondary',
                        minHeight: 'auto',
                        '&:hover': {
                            bgcolor: 'action.hover',
                        }
                    }}
                >
                    Cancel
                </Button>
                
                {CALENDAR_ENABLED && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            background: 'background.paper',
                            borderRadius: '40px',
                            boxShadow: 'none',
                            height: '32px',
                            minWidth: 0,
                            overflow: 'hidden',
                            cursor: getTotalSelectedCount() === 0 ? 'not-allowed' : 'pointer',
                            px: 0,
                            py: 0,
                            border: '1.5px solid #e50c20',
                            transition: 'background 0.2s, color 0.2s',
                            opacity: getTotalSelectedCount() === 0 ? 0.5 : 1,
                            ...(getTotalSelectedCount() > 0 && {
                                '&:hover': {
                                    bgcolor: '#e50c20',
                                    '& .battle-card-btn-text': {
                                        color: '#000',
                                    },
                                },
                            }),
                        }}
                        onClick={() => {
                            if (getValidSelectedCount() > 0) handleAddToCalendar();
                        }}
                    >
                        <Box
                            sx={{
                                background: '#e50c20',
                                width: '2%',
                                minWidth: '24px',
                                height: '100%',
                                borderRadius: '40px 0 0 40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <EventIcon sx={{ fontSize: 14, color: '#fff' }} />
                        </Box>
                        <Typography
                            className="battle-card-btn-text"
                            component="span"
                            sx={{
                                fontSize: '.85rem',
                                fontWeight: 600,
                                pl: 1.5,
                                pr: 2,
                                py: 1,
                                textAlign: 'left',
                                whiteSpace: 'nowrap',
                                color: 'text.primary',
                                userSelect: 'none',
                                transition: 'color 0.2s',
                            }}
                        >
                            Add to Calendar
                        </Typography>
                    </Box>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        background: 'background.paper',
                        borderRadius: '40px',
                        boxShadow: 'none',
                        height: 32,
                        minWidth: 0,
                        overflow: 'hidden',
                        cursor: (getValidSelectedCount() === 0 || isExporting) ? 'not-allowed' : 'pointer',
                        px: 0,
                        py: 0,
                        border: '1.5px solid #e50c20',
                        transition: 'background 0.2s, color 0.2s',
                        opacity: (getValidSelectedCount() === 0 || isExporting) ? 0.5 : 1,
                        ...(getValidSelectedCount() > 0 && !isExporting && {
                            '&:hover': {
                                bgcolor: '#e50c20',
                                '& .battle-card-btn-text': {
                                    color: '#fff',
                                },
                            },
                        }),
                    }}
                    onClick={() => {
                        if (getValidSelectedCount() > 0 && !isExporting) handleExportToPdf();
                    }}
                >
                    <Box
                        sx={{
                            background: '#e50c20',
                            width: '2%',
                            minWidth: '24px',
                            height: '100%',
                            borderRadius: '40px 0 0 40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <PictureAsPdfIcon sx={{ fontSize: 14, color: '#fff' }} />
                    </Box>
                    <Typography
                        className="battle-card-btn-text"
                        component="span"
                        sx={{
                            fontSize: '.85rem',
                            fontWeight: 600,
                            pl: 1.5,
                            pr: 2,
                            py: 1,
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                            color: 'text.primary',
                            userSelect: 'none',
                            transition: 'color 0.2s',
                        }}
                    >
                        {isExporting ? 'Downloading...' : 'Export to PDF'}
                    </Typography>
                </Box>
            </DialogActions>
            
            {/* Calendar Event Creation Dialog */}
            {CALENDAR_ENABLED && (
                <CalendarEventCreationDialog
                    open={calendarDialogOpen}
                    onClose={() => setCalendarDialogOpen(false)}
                    onEventCreated={handleCalendarEventCreated}
                    availableAccounts={availableAccounts}
                    messageContents={convertSectionsToMessageContent()}
                    defaultEventData={{
                        title: `Battle Card Meeting - ${getTotalSelectedCount()} Topics`,
                        description: `Meeting agenda based on selected sections from negotiation analysis`,
                    }}
                />
            )}
        </Dialog>
    );
};

export default BattleCard;